import {
  Fragment,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { toast } from "react-toastify"
import { DotsThreeVertical, FunnelSimple, Plus, StackPlus } from "@phosphor-icons/react"
import { AnimatePresence, motion } from "motion/react"
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import { Button, IconButton, Badge, ToastContent } from "~/components/design-system/components"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { Tooltip } from "~/components/reusable/Tooltip"
import GlobalChannelSelect from "~/components/reusable/GlobalChannelSelect"
import GlobalWarehouseSelect from "~/components/reusable/GlobalWarehouseSelect"
import FilterPopover from "~/components/planning/FilterPopover"
import { FilterPill } from "~/components/planning/FilterPill"
import { GateToggle } from "~/components/planning/GateToggle"
import { ManageView, ManageViewOptions } from "~/components/planning/TableViewSelector"
import { DisplayOptionsPopover } from "~/components/planning/DisplayOptionsPopover"
import { copyViewLink, ViewPill } from "~/components/planning/ViewPill"
import { MenuRowButton, OverflowViewsPopover } from "~/components/planning/OverflowViewsPopover"
import {
  buildViewCommands,
  entryName,
  entryTarget,
  isSameEntry,
  type ViewEntry,
} from "~/components/planning/viewCommands"
import { useRegisterSearchCommands } from "~/components/sidebar/search/useRegisterSearchCommands"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import { useViewHotkeys } from "~/components/planning/useViewHotkeys"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import { useConsumeTable, useTableRegistry } from "~/components/planning/TableRegistryContext"
import { type PresetView, useActivePreset } from "~/components/planning/presetView"
import InspectorToggleButton from "~/components/design-system/pages/internal/InspectorToggleButton"
import { useFilters } from "~/hooks/useFilters"
import { useProductTableAggregations } from "~/hooks/useProductTable"
import { useTableSearch } from "~/hooks/useTableSearch"
import { getViewMetaForTableId, useView, type ViewSaveSource } from "~/hooks/useView"
import { resolveDefaultTarget, useDefaultView } from "~/hooks/useDefaultView"
import {
  encodeViewParam,
  parseViewParam,
  resolveViewTarget,
  targetNamesView,
  type ResolvedViewTarget,
  type ViewTarget,
} from "~/hooks/useViewRoute"
import { useSkuStore } from "~/providers/sku-store-provider"
import {
  groupFiltersByDrag,
  setUniformGate,
  splitFiltersIntoGroupUnits,
  ungroupFilter,
  type Filter,
  type FilterFieldType,
} from "~/utils/filter"
import { type OptionType } from "~/models/enum"
import { type SavedTableView } from "~/server/api/routers/tableView"
import { Separator } from "~/components/reusable/Separator"
import { cn } from "~/utils/helpers"
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router"
import { useViewFavorites } from "~/hooks/useViewFavorites"
import { useOverflowItems } from "~/hooks/useOverflowItems"
import { trackEvent } from "~/hooks/useTrackingService"
import { useUser } from "~/providers/userProvider"
import { resolveViewScope } from "~/server/api/utils/viewAccess"

const withoutTrailingSlash = (path: string) => path.replace(/\/$/, "")

const defaultTargetProperties = (
  resolved: NonNullable<ReturnType<typeof resolveDefaultTarget>>,
) => ({
  "View Name": resolved.kind === "view" ? resolved.view.name : resolved.preset.name,
  "View Type": resolved.kind === "view" ? "Saved View" : "Preset",
})

/** `URL` is any `?view=` change: a shared link, a favorite, history navigation. `Search` is ⌘K. */
type SelectionSource = "Pill" | "URL" | "Search" | "Overflow Menu"

/** Where a pill-menu action (default, copy link) ran: the pill's "…" menu, ⌘K or a V hotkey */
type PillActionSource = "Pill" | "Search" | "Hotkey"

const toastDefaultViewSet = (name: string) =>
  toast.success(
    <ToastContent
      title={`"${name}" set as default view`}
      description="It will open when you return to this page."
    />,
  )

/** The pill the bar shows as selected; a saved view refines its preset, so it wins */
const getActiveEntry = ({
  currentView,
  finalViews,
  supportsViews,
  hasPresets,
  activePreset,
}: {
  currentView: SavedTableView | undefined
  finalViews: SavedTableView[]
  supportsViews: boolean
  hasPresets: boolean
  activePreset: PresetView | undefined | null
}): ViewEntry | null => {
  if (supportsViews && currentView) {
    // `currentView` is this tab's stored copy; the listed row has others' renames and `type`
    const listed = finalViews.find((v) => v.id !== undefined && v.id === currentView.id)
    return { kind: "view", view: listed ?? currentView }
  }
  if (!hasPresets) return { kind: "system" }
  return activePreset ? { kind: "preset", preset: activePreset } : null
}

/** A single entry in the "More actions" overflow menu. */
export type ViewConfigurationAction = {
  id: string
  label: string
  /** Phosphor icon */
  icon?: ReactNode
  onClick: () => void
  /** Count or short text rendered as a badge to the right of the label. */
  badge?: string | number
  disabled?: boolean
  /** Tooltip explaining why the action is disabled. */
  disabledReason?: string
}

type ViewConfigurationProps = {
  tableId?: string
  showFilters?: boolean
  showWarehouseSelect?: boolean
  /** Narrows the warehouse selector to a subset — see `GlobalWarehouseSelect`. */
  warehouseIds?: readonly string[]
  showChannelSelect?: boolean
  extraDisplayOptions?: ReactNode
  extraFilterOptions?: ReactNode
  extraComponent?: ReactNode
  /**
   * Secondary row of page-level actions, rendered on its own line under the main control row and
   * above the filter pills. For action groups that would otherwise crowd `extraComponent` (e.g.
   * the Fashion buy plan's demand-plan actions). Falsy renders no row at all.
   */
  actionRow?: ReactNode
  /** Primary page-level control rendered immediately left of the search field. */
  primaryControl?: ReactNode
  /**
   * Export trigger rendered between the filter button and Display Options popover.
   * Expect an icon-only trigger (e.g. ExportForecastPopover compact).
   */
  exportButton?: ReactNode
  /**
   * Additional page-level actions collected behind a "More actions" icon button rendered
   * left of the inspector toggle. Falsy entries are filtered so callers can gate inline.
   */
  moreActions?: (ViewConfigurationAction | false | null | undefined)[]
  /** Built-in views shipped with the page. First entry is treated as the default. */
  presetViews?: PresetView[]
  /** Scope key for active-preset session storage. Defaults to `tableId`. */
  presetGroupId?: string
  noHorizontalPadding?: boolean
  /** Context-specific filter fields appended to the defaults (e.g. the supply plan's SKU Size). */
  extraFilterFields?: FilterFieldType[]
  /**
   * Keeps active filters when switching preset pills. For pages whose presets are sub-views of one dataset, a
   * planner's filter is page context rather than per-preset state, so clearing it reads as the filter being lost.
   */
  keepFiltersOnPresetChange?: boolean
}

const ViewConfiguration = ({
  tableId,
  showFilters = true,
  showWarehouseSelect = true,
  warehouseIds,
  showChannelSelect = false,
  extraDisplayOptions,
  extraFilterOptions,
  extraComponent,
  actionRow,
  primaryControl,
  exportButton,
  moreActions,
  presetViews,
  presetGroupId,
  noHorizontalPadding = false,
  extraFilterFields,
  keepFiltersOnPresetChange = false,
}: ViewConfigurationProps) => {
  useConsumeTable(tableId)
  const capabilities = useTableRegistry(tableId)

  const additionalPages = useMemo(() => {
    if (!presetViews) return undefined
    const pages = presetViews
      .map((p) => (p.tableId ? getViewMetaForTableId(p.tableId)?.page : undefined))
      .filter((p): p is string => !!p)
    return pages.length > 0 ? pages : undefined
  }, [presetViews])

  const {
    backToDefault,
    currentView,
    viewChange,
    finalViews,
    savedViews,
    supportsViews: viewsRegistered,
    tableView,
    gridTableId,
    isCurrentViewChanged,
    hasActiveGridFilter,
    refetchViews,
    refreshViewsIfStale,
  } = useView({
    filtersEnabled: showFilters,
    channelEnabled: showChannelSelect,
    warehouseEnabled: showWarehouseSelect,
    tableIdOverride: tableId,
    additionalPages,
  })

  const supportsViews = viewsRegistered

  // Non-strict: `view` lives on /_auth and this component doesn't know which route renders it.
  const navigate = useNavigate()
  const viewParam = useSearch({ strict: false, select: (s) => s.view }) as
    | string
    | number
    | undefined

  // Key the default by preset group so multi-preset tabs share one default across their presets.
  const resolvedPresetGroupId = presetGroupId ?? tableId ?? ""
  const {
    setViewDefault,
    setPresetDefault,
    unsetDefault,
    isViewDefault,
    isPresetDefault,
    isSystemDefault,
    defaultTarget,
  } = useDefaultView(resolvedPresetGroupId)

  const { filters, clearAll } = useFilters()
  const selectedChannel = useSkuStore((state) => state.channel)
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)

  const { activePreset, setActivePresetId, defaultPresetId, visiblePresets } = useActivePreset(
    resolvedPresetGroupId,
    presetViews ?? [],
  )
  const presetTableId = (preset: PresetView) => preset.tableId ?? gridTableId
  const [, setAggregations] = useProductTableAggregations(tableId ?? "")

  // `replace` so Back leaves the page instead of stepping through every pill click. The ref
  // suppresses the write while the landing effect applies a target, so the two never race.
  const isApplyingFromUrlRef = useRef(false)
  // The param value this component expects, so the URL-change effect skips its own writes.
  const expectedParamRef = useRef(viewParam)
  const setViewParam = (target: ViewTarget) => {
    if (isApplyingFromUrlRef.current) return
    expectedParamRef.current = encodeViewParam(target)
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, view: encodeViewParam(target) }),
      replace: true,
    })
  }

  const favorites = useViewFavorites(currentView)

  const applyPresetState = (preset: PresetView) => {
    if (preset.state?.aggregations) {
      setAggregations(preset.state.aggregations)
    }
  }

  const handlePresetClick = (preset: PresetView, source: SelectionSource) => {
    if (preset.disabled) return
    trackEvent("Preset Selected", {
      "View Name": preset.name,
      "Table ID": presetTableId(preset),
      Source: source,
    })
    setViewParam({ presetId: preset.id })
    if (preset.id === activePreset?.id) {
      // Reaffirming the active preset: reset to its default state via the current useView.
      backToDefault()
      applyPresetState(preset)
      return
    }
    // Switching to a different preset: clear the regular filters so a saved view's filters
    // don't leak onto the target preset (filters live in a shared store, not per-preset).
    // Channel and warehouse are viewing context and intentionally persist across presets.
    // Per-table column/aggregation layout is also left alone (each preset owns its own).
    if (showFilters && !keepFiltersOnPresetChange) clearAll()
    // Then clear the target's currentView slot so its applyOnMount doesn't auto-restore the
    // last saved view the user had on that preset. A preset pill always lands clean.
    if (preset.tableId) {
      const meta = getViewMetaForTableId(preset.tableId)
      if (meta) window.sessionStorage.removeItem(meta.locationId)
    }
    setActivePresetId(preset.id)
  }

  /** `source` is omitted for a stored default, which is tracked as `Default View Applied`. */
  const handleSavedViewClick = (view: (typeof finalViews)[number], source?: SelectionSource) => {
    if (view.id !== undefined) setViewParam({ viewId: view.id })
    // Find which preset this view belongs to (by matching view.page to a preset's tableId).
    const targetPreset = presetViews?.find((p) => {
      if (!p.tableId) return false
      return getViewMetaForTableId(p.tableId)?.page === view.page
    })
    // Not in `viewChange`: a cross-preset click applies via the remount's untracked restore.
    if (source) {
      trackEvent("View Selected", {
        "View Name": view.name,
        "Table ID": gridTableId,
        Source: source,
      })
    }

    if (targetPreset && targetPreset.id !== activePreset?.id) {
      // Cross-preset click: stash the view in the target preset's currentView slot so
      // its V2 picks it up via applyOnMount, then switch the active preset to remount.
      const meta = targetPreset.tableId ? getViewMetaForTableId(targetPreset.tableId) : null
      if (meta) {
        window.sessionStorage.setItem(meta.locationId, JSON.stringify(view))
      }
      setActivePresetId(targetPreset.id)
      return
    }

    // Same preset (or no preset group): apply via the current useView; don't touch the
    // active preset id so the parent tab doesn't see a transient null state.
    viewChange(view)
  }

  /** `source` is omitted when the view goes away on its own (deleted here or elsewhere). */
  const handleBackToDefault = (source?: "Pill" | "Filter Row" | "Search") => {
    if (source && currentView) {
      trackEvent("View Cleared", {
        "View Name": currentView.name,
        "Table ID": gridTableId,
        Source: source,
      })
    }
    setViewParam(null)
    backToDefault()
    if (activePreset) {
      // Stay on the current preset; re-apply its declarative state.
      applyPresetState(activePreset)
    } else if (presetViews && presetViews.length > 0) {
      setActivePresetId(defaultPresetId)
      const defaultPreset = presetViews.find((p) => p.id === defaultPresetId)
      if (defaultPreset) applyPresetState(defaultPreset)
    }
  }

  /** A param that named nothing available is stale — drop it so the URL matches what's shown. */
  const dropStaleParam = () => {
    setViewParam(null)
    // Presets are skipped: a disabled one may be enabled again.
    const staleTarget = parseViewParam(viewParam)
    if (staleTarget && "viewId" in staleTarget) favorites.removeView(staleTarget.viewId)
  }

  /**
   * Applies a resolved target unless it's already showing; returns whether it applied. `fromUrl`
   * suppresses the param write.
   */
  const applyTarget = (resolved: ResolvedViewTarget, fromUrl: boolean) => {
    // A plain reload: the param names what the mount restore already brought back. A full apply
    // would reset the filters, warehouse and channel changed since.
    const alreadyShowing =
      resolved.kind === "view"
        ? resolved.view.id === currentView?.id
        : !currentView && resolved.preset.id === activePreset?.id
    if (alreadyShowing) {
      // A preset has no `currentView` for the sync effect to mirror, so stamp it here or a copied
      // link carries no view.
      if (!fromUrl && resolved.kind === "preset") setViewParam({ presetId: resolved.preset.id })
      return false
    }

    // A URL target must not write the param back — it's already correct, and the round-trip would
    // race the apply. A stored default does need the write below to reach the URL.
    isApplyingFromUrlRef.current = fromUrl
    try {
      if (resolved.kind === "preset" && fromUrl) {
        // A restored saved view must go first, or the URL-sync effect writes it back over the link.
        if (currentView) backToDefault()
        handlePresetClick(resolved.preset, "URL")
      } else if (resolved.kind === "preset") {
        setViewParam({ presetId: resolved.preset.id })
        setActivePresetId(resolved.preset.id)
        applyPresetState(resolved.preset)
      } else {
        handleSavedViewClick(resolved.view, fromUrl ? "URL" : undefined)
      }
    } finally {
      isApplyingFromUrlRef.current = false
    }
    return true
  }

  /** Applies the view `?view=` names, or drops the param if it names nothing available. */
  const applyFromUrl = () => {
    const resolved = resolveViewTarget({
      target: parseViewParam(viewParam),
      finalViews,
      presets: presetViews ?? [],
    })
    if (!resolved) {
      // A view missing from a possibly cached list may just be new (e.g. saved in another tab), so
      // only a fresh list can prove it gone. Returns true while that check is pending.
      const target = parseViewParam(viewParam)
      const refresh = target && "viewId" in target ? refreshViewsIfStale() : undefined
      if (refresh) {
        void refresh.then(() => setViewRecheck((n) => n + 1))
        return true
      }
      if (viewParam !== undefined) dropStaleParam()
      return false
    }
    // The list may be cached; a fresh one lets the vanished-view effect catch deletions by others.
    if (resolved.kind === "view") refreshViewsIfStale()
    applyTarget(resolved, true)
    return true
  }

  const [viewRecheck, setViewRecheck] = useState(0)
  const onViewRecheck = useEffectEvent(() => applyFromUrl())
  useEffect(() => {
    if (viewRecheck > 0) onViewRecheck()
  }, [viewRecheck])

  // Seed the view on landing, once per mount.
  const defaultAppliedRef = useRef(false)
  const [landingSettled, setLandingSettled] = useState(false)
  const viewsReady = !supportsViews || savedViews !== undefined
  const onLanding = useEffectEvent(() => {
    // Set before the bail so pages without a preset group still release the sync effect.
    setLandingSettled(true)
    if (!resolvedPresetGroupId) return

    // A deep link is explicit and applies over whatever this tab holds; the stored default is only
    // a fallback, so it defers to a view the user switched to this session.
    if (applyFromUrl()) return

    const presets = presetViews ?? []
    const hasPresetsNow = presets.length > 0
    const atSystemDefault = !currentView && (!hasPresetsNow || activePreset?.id === defaultPresetId)
    if (!atSystemDefault) return
    const resolved = resolveDefaultTarget({ target: defaultTarget, finalViews, presets })
    // An unavailable default keeps whatever is showing.
    if (resolved && applyTarget(resolved, false)) {
      trackEvent("Default View Applied", {
        ...defaultTargetProperties(resolved),
        "Table ID": resolved.kind === "view" ? gridTableId : presetTableId(resolved.preset),
      })
    }
  })
  useEffect(() => {
    if (defaultAppliedRef.current || !viewsReady) return
    defaultAppliedRef.current = true
    onLanding()
  }, [viewsReady])

  // Applies a `?view=` change while mounted, ignoring the next page's param mid-navigation. Compares
  // the leaf match, not this component's: tab content can render in a layout's match.
  const isOwnLocation = useRouterState({
    select: (s) =>
      s.status !== "pending" &&
      withoutTrailingSlash(s.matches.at(-1)?.pathname ?? "") ===
        withoutTrailingSlash(s.location.pathname),
  })
  const onViewParamChange = useEffectEvent(() => {
    if (landingSettled) applyFromUrl()
  })
  useEffect(() => {
    if (!isOwnLocation || viewParam === expectedParamRef.current) return
    expectedParamRef.current = viewParam
    onViewParamChange()
  }, [viewParam, isOwnLocation])

  // Saving a view makes it current inside useView, bypassing the click handlers, so the param would
  // otherwise still name the previous view. Gated on state, not the derived `viewsReady`: the
  // extra commit lets landing settle before `currentView` — read from session storage on the
  // first render, long before `savedViews` — buries a deep link.
  useEffect(() => {
    if (!landingSettled || currentView?.id === undefined) return
    if (targetNamesView(parseViewParam(viewParam), currentView.id)) return
    setViewParam({ viewId: currentView.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingSettled, currentView?.id])

  // The URL's view vanishing from a refetch of the same pages means it was deleted or unshared;
  // a just-created view or another table's list doesn't count.
  const viewPagesKey = [tableView?.id, ...(additionalPages ?? [])].join(",")
  const prevViewListRef = useRef<{ pagesKey: string; ids: Set<number> } | null>(null)
  const onViewListChange = useEffectEvent(() => {
    if (savedViews === undefined) return
    const ids = new Set(finalViews.flatMap((v) => (v.id === undefined ? [] : [v.id])))
    const prev = prevViewListRef.current
    prevViewListRef.current = { pagesKey: viewPagesKey, ids }
    const prevIds = prev?.pagesKey === viewPagesKey ? prev.ids : new Set<number>()
    const target = parseViewParam(viewParam)
    if (!landingSettled || !target || !("viewId" in target)) return
    if (!prevIds.has(target.viewId) || ids.has(target.viewId)) return
    if (currentView?.id === target.viewId) handleBackToDefault()
    dropStaleParam()
  })
  useEffect(() => onViewListChange(), [finalViews])

  const togglePresetDefault = (preset: PresetView, source: PillActionSource) => {
    const eventProperties = {
      "View Name": preset.name,
      "View Type": "Preset",
      "Table ID": presetTableId(preset),
      Source: source,
    }
    if (isPresetDefault(preset.id)) {
      unsetDefault()
      trackEvent("Default View Removed", eventProperties)
      toast.success("Default view removed")
    } else {
      setPresetDefault(preset.id)
      trackEvent("Default View Set", eventProperties)
      toastDefaultViewSet(preset.name)
    }
  }

  const toggleViewDefault = (view: (typeof finalViews)[number], source: PillActionSource) => {
    const eventProperties = {
      "View Name": view.name,
      "View Type": "Saved View",
      "Table ID": gridTableId,
      Source: source,
    }
    if (isViewDefault(view.id)) {
      unsetDefault()
      trackEvent("Default View Removed", eventProperties)
      toast.success("Default view removed")
    } else if (view.id !== undefined) {
      setViewDefault(view.id)
      trackEvent("Default View Set", eventProperties)
      toastDefaultViewSet(view.name)
    }
  }

  const resetToSystemDefault = (source: PillActionSource) => {
    if (isSystemDefault) return
    const removed = resolveDefaultTarget({
      target: defaultTarget,
      finalViews,
      presets: presetViews ?? [],
    })
    unsetDefault()
    trackEvent("Default View Removed", {
      ...(removed && defaultTargetProperties(removed)),
      "Table ID": gridTableId,
      Source: source,
    })
    toast.success("Reset to the system default view")
  }

  const trackLinkCopied = (entry: ViewEntry, source: PillActionSource) =>
    trackEvent("View Link Copied", {
      "View Name": entryName(entry),
      "View Type": entry.kind === "preset" ? "Preset" : "Saved View",
      "Table ID": entry.kind === "preset" ? presetTableId(entry.preset) : gridTableId,
      Source: source,
    })

  const isEntryDefault = (entry: ViewEntry) => {
    if (entry.kind === "view") return isViewDefault(entry.view.id)
    if (entry.kind === "preset") return isPresetDefault(entry.preset.id)
    return isSystemDefault
  }

  const toggleEntryDefault = (entry: ViewEntry, source: PillActionSource) => {
    if (entry.kind === "view") toggleViewDefault(entry.view, source)
    else if (entry.kind === "preset") togglePresetDefault(entry.preset, source)
    else resetToSystemDefault(source)
  }

  const selectEntryFromSearch = (entry: ViewEntry) => {
    if (entry.kind === "view") handleSavedViewClick(entry.view, "Search")
    else if (entry.kind === "preset") handlePresetClick(entry.preset, "Search")
    else handleBackToDefault("Search")
  }

  const showClearView =
    !!currentView ||
    (showChannelSelect && !!selectedChannel) ||
    (showWarehouseSelect && !!selectedWarehouseId) ||
    (showFilters && filters.length !== 0) ||
    hasActiveGridFilter

  const hasPresets = visiblePresets.length > 0

  const { userId } = useUser()
  const coveredByDialog = useIsCoveredByFullPageDialog()
  const hotkeysEnabled = !coveredByDialog
  const [editingViewId, setEditingViewId] = useState<number | null>(null)
  /** Who opened the "Save as new view" dialog; null while closed */
  const [saveViewSource, setSaveViewSource] = useState<ViewSaveSource | null>(null)
  const activeEntry = getActiveEntry({
    currentView,
    finalViews,
    supportsViews,
    hasPresets,
    activePreset,
  })
  const activeViewId = activeEntry?.kind === "view" ? activeEntry.view.id : undefined
  // Switching views closes the edit dialog, even mid-save, so the save can't pull the user back
  if (editingViewId !== null && editingViewId !== activeViewId) setEditingViewId(null)
  const activeTarget = activeEntry && entryTarget(activeEntry)
  const isActiveFavorite = !!activeTarget && favorites.isFavorite(activeTarget)
  const activeScope =
    activeEntry?.kind === "view" ? resolveViewScope(activeEntry.view, userId) : undefined
  const viewCommandsKey = [
    ...visiblePresets.map((p) => [p.id, p.name, p.disabled, p.tooltip].join("|")),
    ...(supportsViews ? finalViews.map((v) => [v.id, v.name, v.type].join("|")) : []),
  ].join(",")

  /** The view commands, with favorite/default/copy link tracked as coming from `source` */
  const viewCommandsFor = (source: "Search" | "Hotkey") =>
    buildViewCommands({
      active: activeEntry,
      presets: visiblePresets,
      views: supportsViews ? finalViews : [],
      supportsViews,
      isDefault: isEntryDefault,
      isFavorite: isActiveFavorite,
      activeScope,
      actions: {
        toggleFavorite: () => {
          if (activeEntry && activeTarget) {
            favorites.toggle(activeTarget, entryName(activeEntry), source)
          }
        },
        toggleDefault: () => activeEntry && toggleEntryDefault(activeEntry, source),
        copyLink: () => {
          if (activeEntry && activeTarget) {
            void copyViewLink(activeTarget, () => trackLinkCopied(activeEntry, source))
          }
        },
        edit: () => {
          if (activeViewId !== undefined) setEditingViewId(activeViewId)
        },
        saveAs: () => setSaveViewSource("Search"),
        select: selectEntryFromSearch,
      },
    })

  const pillRowRef = useRef<HTMLDivElement>(null)
  const activeViewIndex = supportsViews
    ? finalViews.findIndex((v) => v.id !== undefined && v.id === activeViewId)
    : -1
  const isViewVisible = useOverflowItems(
    pillRowRef,
    supportsViews ? finalViews.length : 0,
    activeViewIndex,
  )
  const overflowViews = supportsViews ? finalViews.filter((_, index) => !isViewVisible(index)) : []

  useViewHotkeys(() => viewCommandsFor("Hotkey"), hotkeysEnabled)
  useRegisterSearchCommands(viewCommandsFor("Search"), [
    supportsViews,
    activeEntry?.kind,
    activeEntry && entryName(activeEntry),
    activeTarget && encodeViewParam(activeTarget),
    viewCommandsKey,
    defaultTarget,
    isActiveFavorite,
    activeScope,
  ])

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "flex flex-row items-center w-full py-2.5 gap-3 justify-between",
          noHorizontalPadding ? "px-0" : "px-4",
        )}
        data-view-configuration-v2="true"
      >
        <div
          ref={pillRowRef}
          className="relative flex flex-row gap-3 items-center min-w-0 overflow-hidden flex-1 p-2 -m-2"
        >
          {hasPresets ? (
            visiblePresets.map((preset) => (
              <Fragment key={preset.id}>
                {preset.separatorBefore && (
                  <Separator orientation="vertical" className="h-4 bg-border-base" />
                )}
                <ViewPill
                  label={preset.name}
                  // A saved view refines its preset rather than replacing it, so both stay selected.
                  isSelected={
                    activePreset?.id === preset.id &&
                    (!currentView || currentView.page === tableView?.id)
                  }
                  onClick={() => handlePresetClick(preset, "Pill")}
                  disabled={preset.disabled}
                  tooltip={preset.tooltip}
                  hakioOnly={preset.hakioOnly}
                  icon={preset.icon}
                  badge={preset.badge}
                  badgeVariant={preset.badgeVariant}
                  preset={preset}
                  showDefaultOption
                  isDefault={isPresetDefault(preset.id)}
                  onToggleDefault={() => togglePresetDefault(preset, "Pill")}
                  linkTarget={{ presetId: preset.id }}
                  isFavorite={favorites.isFavorite({ presetId: preset.id })}
                  onToggleFavorite={() => favorites.toggle({ presetId: preset.id }, preset.name)}
                  onLinkCopied={() => trackLinkCopied({ kind: "preset", preset }, "Pill")}
                  shortcutsActive={
                    hotkeysEnabled && isSameEntry(activeEntry, { kind: "preset", preset })
                  }
                />
              </Fragment>
            ))
          ) : (
            <ViewPill
              label="Default"
              isSelected={!currentView}
              onClick={() => handleBackToDefault("Pill")}
              showDefaultOption
              // Default only when no custom default is set; toggling clears any custom default.
              isDefault={isSystemDefault}
              onToggleDefault={() => resetToSystemDefault("Pill")}
              // Its only item is Default, which V D can't undo once it is the default
              shortcutsActive={hotkeysEnabled && activeEntry?.kind === "system" && !isSystemDefault}
            />
          )}
          {supportsViews && (
            <>
              {hasPresets && finalViews.length > 0 && (
                <Separator orientation="vertical" className="h-4 bg-border-base" />
              )}
              {finalViews.map((view, index) => {
                const target = view.id !== undefined ? { viewId: view.id } : null
                return (
                  <div
                    key={view.id ?? view.name}
                    data-overflow-item=""
                    className={cn(
                      "shrink-0",
                      !isViewVisible(index) && "absolute invisible pointer-events-none",
                    )}
                  >
                    <ViewPill
                      label={view.name}
                      isSelected={currentView?.id === view.id}
                      onClick={() => handleSavedViewClick(view, "Pill")}
                      view={view}
                      tableIdOverride={tableId}
                      gridTableId={gridTableId}
                      // Forward this page's extra field definitions (e.g. the buy plan's "SKU
                      // Size") so the details hover card can label saved filters on those fields.
                      extraFilterFields={extraFilterFields}
                      showDefaultOption
                      isDefault={isViewDefault(view.id)}
                      onToggleDefault={() => toggleViewDefault(view, "Pill")}
                      linkTarget={target ?? undefined}
                      isFavorite={!!target && favorites.isFavorite(target)}
                      onToggleFavorite={
                        target ? () => favorites.toggle(target, view.name) : undefined
                      }
                      onLinkCopied={() => trackLinkCopied({ kind: "view", view }, "Pill")}
                      shortcutsActive={
                        hotkeysEnabled && isSameEntry(activeEntry, { kind: "view", view })
                      }
                      onDeleted={() => {
                        if (view.id === currentView?.id) handleBackToDefault()
                        refetchViews()
                      }}
                    />
                  </div>
                )
              })}
              <div
                data-overflow-more=""
                className={cn(
                  "shrink-0",
                  overflowViews.length === 0 && "absolute invisible pointer-events-none",
                )}
              >
                <OverflowViewsPopover
                  views={overflowViews}
                  userId={userId}
                  onSelect={(view) => handleSavedViewClick(view, "Overflow Menu")}
                  onSaveView={() => setSaveViewSource("Overflow Menu")}
                />
              </div>
              <ManageView
                mode="create"
                tableIdOverride={tableId}
                baseLabel={activePreset?.name}
                source={saveViewSource ?? "Save Button"}
                open={saveViewSource !== null}
                onOpenChange={(open) =>
                  setSaveViewSource((prev) => (open ? (prev ?? "Save Button") : null))
                }
                triggerTooltip="Save as new view"
                trigger={
                  <IconButton
                    variant="transparent"
                    size="sm"
                    aria-label="Save current state as new view"
                  >
                    <StackPlus />
                  </IconButton>
                }
              />
              {/* Mounted only while open: each ManageView runs its own useView */}
              {editingViewId !== null && activeEntry?.kind === "view" && (
                <ManageView
                  key={editingViewId}
                  mode="update"
                  source="Search"
                  view={activeEntry.view}
                  tableIdOverride={tableId}
                  open
                  onOpenChange={(open) => {
                    if (!open) setEditingViewId(null)
                  }}
                />
              )}
            </>
          )}
        </div>
        <div className="flex flex-row items-center gap-2 shrink-0">
          {extraComponent}
          {showWarehouseSelect && <GlobalWarehouseSelect warehouseIds={warehouseIds} />}
          {showChannelSelect && <GlobalChannelSelect />}
          {primaryControl}
          {tableId && capabilities?.search && (
            <TableSearchField
              tableId={tableId}
              options={capabilities.search.options}
              placeholder={capabilities.search.placeholder}
              onBeforeChange={capabilities.search.onBeforeChange}
            />
          )}
          {showFilters && (
            <FilterPopover
              align="end"
              shortcutEnabled
              registerSearchCommands
              extraFilterFields={extraFilterFields}
              extraFilterOptions={extraFilterOptions}
              customTrigger={
                <IconButton variant="neutral" size="sm" aria-label="Add filter">
                  <FunnelSimple />
                </IconButton>
              }
            />
          )}
          {exportButton}
          <DisplayOptionsPopover tableId={tableId} extraOptions={extraDisplayOptions} />
          {moreActions && <MoreActionsPopover actions={moreActions} />}
          <InspectorToggleButton />
        </div>
      </div>
      {actionRow && (
        <div
          className={cn(
            "flex flex-row items-center justify-end gap-2 pb-2.5",
            noHorizontalPadding ? "px-0" : "px-4",
          )}
        >
          {actionRow}
        </div>
      )}
      <AnimatePresence initial={false}>
        {showFilters &&
          (filters.length > 0 ||
            (!!currentView && isCurrentViewChanged) ||
            hasActiveGridFilter) && (
            <FilterRowAnimated
              filters={filters}
              supportsViews={supportsViews}
              isCurrentViewChanged={isCurrentViewChanged}
              hasCurrentView={!!currentView}
              showClearView={showClearView}
              backToDefault={() => handleBackToDefault("Filter Row")}
              tableIdOverride={tableId}
              baseLabel={activePreset?.name}
              extraFilterFields={extraFilterFields}
            />
          )}
      </AnimatePresence>
    </div>
  )
}

const MoreActionsPopover = ({
  actions,
}: {
  actions: (ViewConfigurationAction | false | null | undefined)[]
}) => {
  const [open, setOpen] = useState(false)
  const finalActions = actions.filter((a): a is ViewConfigurationAction => !!a)
  if (finalActions.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ShortcutTooltip label="More actions">
        <PopoverTrigger asChild>
          <IconButton variant="neutral" size="sm" aria-label="More actions">
            <DotsThreeVertical />
          </IconButton>
        </PopoverTrigger>
      </ShortcutTooltip>
      <PopoverContent align="end" sideOffset={8} className="w-[220px] p-1 flex flex-col">
        {finalActions.map((action) => {
          const item = (
            <MenuRowButton
              key={action.id}
              disabled={action.disabled}
              onClick={() => {
                setOpen(false)
                action.onClick()
              }}
            >
              {action.icon}
              <span className="truncate flex-1">{action.label}</span>
              {action.badge !== undefined && (
                <Badge variant="gray" size="sm">
                  {action.badge}
                </Badge>
              )}
            </MenuRowButton>
          )
          return action.disabled && action.disabledReason ? (
            <Tooltip key={action.id} title={action.disabledReason}>
              {item}
            </Tooltip>
          ) : (
            item
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

type FilterRowProps = {
  filters: Filter[]
  supportsViews: boolean
  isCurrentViewChanged: boolean
  hasCurrentView: boolean
  showClearView: boolean
  backToDefault: () => void
  tableIdOverride?: string
  baseLabel?: string
  extraFilterFields?: FilterFieldType[]
}

export const FilterRowAnimated = (props: FilterRowProps) => {
  const [animating, setAnimating] = useState(true)
  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={() => setAnimating(false)}
      style={{ overflow: animating ? "hidden" : "visible" }}
    >
      <FilterRow {...props} />
    </motion.div>
  )
}

/** Matches the container's gap-2 so an entering/exiting zero-width pill doesn't add a double gap. */
const PILL_GAP_PX = 8

const AnimatedFilterPill = ({ children }: { children: ReactNode }) => {
  const [animating, setAnimating] = useState(false)
  return (
    <motion.div
      initial={{ width: 0, opacity: 0, marginLeft: -PILL_GAP_PX }}
      animate={{ width: "auto", opacity: 1, marginLeft: 0 }}
      exit={{ width: 0, opacity: 0, marginLeft: -PILL_GAP_PX }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={() => setAnimating(false)}
      className="shrink-0 max-w-full"
      style={{ overflow: animating ? "hidden" : "visible" }}
    >
      {children}
    </motion.div>
  )
}

const DraggableFilterPill = ({
  index,
  isDropTarget,
  ungroupHint,
  children,
}: {
  index: number
  isDropTarget?: boolean
  ungroupHint?: boolean
  children: ReactNode
}) => {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: index })
  const { setNodeRef: setDropRef } = useDroppable({ id: index })

  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node)
    setDropRef(node)
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        "relative flex items-center rounded-full touch-none cursor-grab active:cursor-grabbing transition-shadow",
        isDropTarget && "ring-2 ring-icon-green ring-offset-2 ring-offset-base",
        isDragging && ungroupHint && "ring-2 ring-icon-orange ring-offset-2 ring-offset-base",
      )}
    >
      <span className={cn("inline-flex", isDragging && "opacity-60")}>{children}</span>
    </div>
  )
}

const getDropTargetIndex = (event: DragOverEvent | DragEndEvent): number | null => {
  const { active, over } = event
  if (!over || over.id === active.id) return null
  return Number(over.id)
}

const FilterRow = ({
  filters,
  supportsViews,
  isCurrentViewChanged,
  hasCurrentView,
  showClearView,
  backToDefault,
  tableIdOverride,
  baseLabel,
  extraFilterFields,
}: FilterRowProps) => {
  const { setFilters } = useFilters()

  const toggleFilterGate = (index: number) => {
    const next = (filters[index]?.gate ?? "and") === "and" ? "or" : "and"
    setFilters(setUniformGate(filters, index, next))
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const activeFilter = activeIndex != null ? filters[activeIndex] : null
  const ungroupHint =
    activeIndex != null && dropTargetIndex == null && filters[activeIndex]?.groupId != null

  const targetFilter = dropTargetIndex != null ? filters[dropTargetIndex] : null
  const targetInGroup =
    targetFilter?.groupId != null &&
    filters.filter((f) => f.groupId === targetFilter.groupId).length > 1
  const overlayHint: { text: string; tone: "green" | "orange" } | null = ungroupHint
    ? { text: "Leave group", tone: "orange" }
    : targetFilter
      ? { text: targetInGroup ? "Add to group" : "Group", tone: "green" }
      : null

  const handleDragEnd = (event: DragEndEvent) => {
    setDropTargetIndex(null)
    setActiveIndex(null)
    const from = Number(event.active.id)
    const target = getDropTargetIndex(event)

    if (target == null) {
      if (event.over == null && filters[from]?.groupId != null) {
        setFilters(ungroupFilter(filters, from))
      }
      return
    }
    setFilters(groupFiltersByDrag(filters, from, target))
  }

  const filterUnits = splitFiltersIntoGroupUnits(filters)

  return (
    <div className="flex flex-row items-center justify-between gap-2 px-4 py-1.5 min-h-10 border-t border-base">
      <DndContext
        sensors={sensors}
        onDragStart={(event) => setActiveIndex(Number(event.active.id))}
        onDragOver={(event) => setDropTargetIndex(getDropTargetIndex(event))}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setDropTargetIndex(null)
          setActiveIndex(null)
        }}
      >
        <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1 p-2 -m-2">
          <AnimatePresence initial={false}>
            {filterUnits.map((unit, unitIndex) => {
              const firstIndex = filters.indexOf(unit.filters[0]!)
              const isGroup = unit.filters.length > 1
              const groupIsDropTarget =
                isGroup && unit.filters.some((f) => dropTargetIndex === filters.indexOf(f))
              return [
                unitIndex > 0 ? (
                  <GateToggle key={`gate-${firstIndex}`} gate="and" readOnly />
                ) : null,
                <AnimatedFilterPill key={`unit-${unitIndex}-${unit.filters[0]?.field ?? ""}`}>
                  {isGroup ? (
                    <div
                      className={cn(
                        "relative flex flex-row flex-wrap items-center gap-2 rounded-lg border border-dashed border-strong px-1.5 py-0.5 transition-shadow",
                        groupIsDropTarget &&
                          "border-solid ring-2 ring-icon-green ring-offset-2 ring-offset-base",
                      )}
                    >
                      {unit.filters.map((filter, filterIndex) => (
                        <Fragment key={`${filterIndex}-${filter.field}`}>
                          {filterIndex > 0 &&
                            (filterIndex === 1 ? (
                              <GateToggle
                                gate={filter.gate ?? "or"}
                                onToggle={() => toggleFilterGate(filters.indexOf(filter))}
                              />
                            ) : (
                              <GateToggle gate={filter.gate ?? "or"} readOnly />
                            ))}
                          <DraggableFilterPill
                            index={filters.indexOf(filter)}
                            ungroupHint={ungroupHint}
                          >
                            <FilterPopover
                              editingFilter={filter}
                              editingFilterIndex={filters.indexOf(filter)}
                              editGroup
                              extraFilterFields={extraFilterFields}
                            />
                          </DraggableFilterPill>
                        </Fragment>
                      ))}
                    </div>
                  ) : (
                    unit.filters[0] && (
                      <DraggableFilterPill
                        index={filters.indexOf(unit.filters[0])}
                        isDropTarget={dropTargetIndex === filters.indexOf(unit.filters[0])}
                      >
                        <FilterPopover
                          editingFilter={unit.filters[0]}
                          editingFilterIndex={filters.indexOf(unit.filters[0])}
                          extraFilterFields={extraFilterFields}
                        />
                      </DraggableFilterPill>
                    )
                  )}
                </AnimatedFilterPill>,
              ]
            })}
          </AnimatePresence>
          <FilterPopover
            shortcutEnabled
            extraFilterFields={extraFilterFields}
            customTrigger={
              <FilterPill
                size="xs"
                variant="neutralOutline"
                icon={<Plus size={16} />}
                className="flex items-center justify-center"
              />
            }
          />
        </div>
        <DragOverlay dropAnimation={null}>
          {activeFilter ? (
            <div className="relative rounded-full shadow-flyout cursor-grabbing">
              {overlayHint && (
                <span
                  className={cn(
                    "absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none shadow-flyout",
                    overlayHint.tone === "orange"
                      ? "border-badge-orange-border bg-badge-orange-bg text-badge-orange-fg-text"
                      : "border-badge-green-border bg-badge-green-bg text-badge-green-fg-text",
                  )}
                >
                  {overlayHint.text}
                </span>
              )}
              <FilterPopover
                editingFilter={activeFilter}
                editingFilterIndex={activeIndex!}
                editGroup={activeFilter.groupId != null}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <div className="flex flex-row items-center gap-2 shrink-0">
        {showClearView && (
          <Button
            size="sm"
            variant="transparent"
            onClick={(e) => {
              e.preventDefault()
              backToDefault()
            }}
          >
            Clear view
          </Button>
        )}
        <ManageViewOptions
          supportsViews={supportsViews}
          isCurrentViewChanged={isCurrentViewChanged}
          hasCurrentView={hasCurrentView}
          tableIdOverride={tableIdOverride}
          baseLabel={baseLabel}
        />
      </div>
    </div>
  )
}

const TableSearchField = ({
  tableId,
  options,
  placeholder = "Search...",
  onBeforeChange,
}: {
  tableId: string
  options?: OptionType[]
  placeholder?: string
  onBeforeChange?: () => void
}) => {
  const { searchText, setSearchText, searchBy, setSearchBy } = useTableSearch(tableId)
  return (
    <ExpandableSearchField
      initialSearchText={searchText}
      onSearchTextChange={(value) => {
        onBeforeChange?.()
        setSearchText(value)
      }}
      placeholder={placeholder}
      searchBy={options ? searchBy : undefined}
      onSearchByChange={
        options
          ? (value) => {
              onBeforeChange?.()
              setSearchBy(value)
            }
          : undefined
      }
      searchByOptions={options}
    />
  )
}

export default ViewConfiguration
